import type { CssGeneratorContext } from '../css-generator.model';

/**
 * Plain `<table class="app-table">` markup with `app-table-thead` / `app-table-tbody` sections gets the
 * same token-based cell, row and table declarations the library data table gives its own table, thead
 * and tbody elements, including the variants a surrounding library data table switches on (hoverable
 * rows, gridlines, small and large cells).
 */
export const styles = ({ dt }: CssGeneratorContext) => `
.app-table {
	border-spacing: 0;
	border-collapse: separate;
	width: 100%;
}

.app-table-tbody > tr {
	outline-color: transparent;
	background: ${dt('datatable.row.background')};
	color: ${dt('datatable.row.color')};
	transition:
		background ${dt('datatable.transition.duration')},
		color ${dt('datatable.transition.duration')},
		border-color ${dt('datatable.transition.duration')},
		outline-color ${dt('datatable.transition.duration')},
		box-shadow ${dt('datatable.transition.duration')};
}

.app-table-tbody > tr > td {
	text-align: start;
	border-color: ${dt('datatable.body.cell.border.color')};
	border-style: solid;
	border-width: 0 0 1px 0;
	padding: ${dt('datatable.body.cell.padding')};
}

.p-datatable-hoverable .app-table-tbody > tr:not(.p-datatable-row-selected):hover {
	background: ${dt('datatable.row.hover.background')};
	color: ${dt('datatable.row.hover.color')};
}

.app-table-tbody > tr:focus-visible {
	box-shadow: ${dt('datatable.row.focus.ring.shadow')};
	outline: ${dt('datatable.row.focus.ring.width')} ${dt('datatable.row.focus.ring.style')} ${dt('datatable.row.focus.ring.color')};
	outline-offset: ${dt('datatable.row.focus.ring.offset')};
}

.p-datatable-gridlines .app-table-thead > tr > th {
	border-width: 1px 0 1px 1px;
}

.p-datatable-gridlines .app-table-thead > tr > th:last-child {
	border-width: 1px;
}

.p-datatable-gridlines .app-table-tbody > tr > td {
	border-width: 1px 0 0 1px;
}

.p-datatable-gridlines .app-table-tbody > tr > td:last-child {
	border-width: 1px 1px 0 1px;
}

.p-datatable-gridlines .app-table-tbody > tr:last-child > td {
	border-width: 1px 0 1px 1px;
}

.p-datatable-gridlines .app-table-tbody > tr:last-child > td:last-child {
	border-width: 1px;
}

.p-datatable.p-datatable-gridlines:has(.p-datatable-thead):has(.p-datatable-tbody) .app-table-tbody > tr > td {
	border-width: 0 0 1px 1px;
}

.p-datatable.p-datatable-gridlines:has(.p-datatable-thead):has(.p-datatable-tbody) .app-table-tbody > tr > td:last-child {
	border-width: 0 1px 1px 1px;
}

.p-datatable.p-datatable-gridlines:has(.p-datatable-tbody):has(.p-datatable-tfoot) .app-table-tbody > tr:last-child > td {
	border-width: 0 0 0 1px;
}

.p-datatable.p-datatable-gridlines:has(.p-datatable-tbody):has(.p-datatable-tfoot) .app-table-tbody > tr:last-child > td:last-child {
	border-width: 0 1px 0 1px;
}

.p-datatable-striped.p-datatable-hoverable .app-table-tbody > tr:not(.p-datatable-row-selected):hover {
	background: ${dt('datatable.row.hover.background')};
	color: ${dt('datatable.row.hover.color')};
}

.p-datatable.p-datatable-sm .app-table-thead > tr > th {
	padding: ${dt('datatable.header.cell.sm.padding')};
}

.p-datatable.p-datatable-sm .app-table-tbody > tr > td {
	padding: ${dt('datatable.body.cell.sm.padding')};
}

.p-datatable.p-datatable-lg .app-table-thead > tr > th {
	padding: ${dt('datatable.header.cell.lg.padding')};
}

.p-datatable.p-datatable-lg .app-table-tbody > tr > td {
	padding: ${dt('datatable.body.cell.lg.padding')};
}
`;
